import {
  EdgelessIcon,
  ToggleCollapseIcon,
  ViewLayersIcon,
} from '@blocksuite/icons/rc';
import type { DocCollection, DocMeta } from '@blocksuite/store';
import { Note04Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import type { Tag } from '@pique/env/filter';
import { useI18n } from '@pique/i18n';
import { DocsService, useLiveData, useService } from '@toeverything/infra';
import { selectAtom } from 'jotai/utils';
import type { MouseEventHandler } from 'react';
import { memo, useCallback, useMemo } from 'react';

import { CollectionListItem } from './collections/collection-list-item';
import { PageListItem } from './docs/page-list-item';
import { PagePreview } from './page-content-preview';
import * as styles from './page-group.css';
import {
  groupCollapseStateAtom,
  listPropsAtom,
  pageItemIdsAtom,
  selectionStateAtom,
  useAtom,
  useAtomValue,
} from './scoped-atoms';
import { TagListItem } from './tags/tag-list-item';
import type {
  CollectionListItemProps,
  CollectionMeta,
  ItemGroupProps,
  ListItem,
  ListProps,
  PageListItemProps,
  TagListItemProps,
  TagMeta,
} from './types';
import { shallowEqual } from './utils';

export const ItemGroupHeader = memo(function ItemGroupHeader<
  T extends ListItem,
>({ id, items, label }: ItemGroupProps<T>) {
  const [collapseState, setCollapseState] = useAtom(groupCollapseStateAtom);
  const collapsed = collapseState[id];
  const onExpandedClicked: MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      setCollapseState(v => ({ ...v, [id]: !v[id] }));
    },
    [id, setCollapseState]
  );

  const [selectionState, setSelectionActive] = useAtom(selectionStateAtom);
  const selectedItems = useMemo(() => {
    const selectedIds = selectionState.selectedIds ?? [];
    return items.filter(item => selectedIds.includes(item.id));
  }, [items, selectionState.selectedIds]);

  const allSelected = selectedItems.length === items.length;

  const onSelectAll = useCallback(() => {
    setSelectionActive(true);

    const nonCurrentGroupIds =
      selectionState.selectedIds?.filter(
        id => !items.map(item => item.id).includes(id)
      ) ?? [];

    const newSelectedPageIds = allSelected
      ? nonCurrentGroupIds
      : [...nonCurrentGroupIds, ...items.map(item => item.id)];

    selectionState.onSelectedIdsChange?.(newSelectedPageIds);
  }, [setSelectionActive, selectionState, allSelected, items]);

  const t = useI18n();

  return label ? (
    <div
      data-testid="page-list-group-header"
      className={styles.header}
      data-group-id={id}
      data-group-items-count={items.length}
      data-group-selected-items-count={selectedItems.length}
    >
      <div
        role="button"
        onClick={onExpandedClicked}
        data-testid="page-list-group-header-collapsed-button"
        className={styles.collapsedIconContainer}
      >
        <ToggleCollapseIcon
          className={styles.collapsedIcon}
          data-collapsed={!!collapsed}
        />
      </div>
      <div className={styles.headerLabel}>{label}</div>
      {selectionState.selectionActive ? (
        <div className={styles.headerCount}>
          {selectedItems.length}/{items.length}
        </div>
      ) : null}
      <div className={styles.spacer} />
      <button className={styles.selectAllButton} onClick={onSelectAll}>
        {t[
          allSelected
            ? 'com.pique.page.group-header.clear'
            : 'com.pique.page.group-header.select-all'
        ]()}
      </button>
    </div>
  ) : null;
}) as <T extends ListItem>(
  props: ItemGroupProps<T>
) => React.ReactElement | null;

const requiredPropNames = [
  'docCollection',
  'rowAsLink',
  'operationsRenderer',
  'selectedIds',
  'onSelectedIdsChange',
  'draggable',
] as const;

type RequiredProps<T> = Pick<
  ListProps<T>,
  (typeof requiredPropNames)[number]
> & {
  selectable: boolean;
};

const listsPropsAtom = selectAtom(
  listPropsAtom,
  props => {
    return Object.fromEntries(
      requiredPropNames.map(name => [name, props?.[name]])
    ) as RequiredProps<ListItem>;
  },
  shallowEqual
);

function makeToggleSelection(
  itemId: string,
  selectedIds: string[] | undefined,
  onSelectedIdsChange: ((ids: string[]) => void) | undefined
): (() => void) | undefined {
  if (!onSelectedIdsChange || !selectedIds) return undefined;
  return () => {
    if (selectedIds.includes(itemId)) {
      onSelectedIdsChange(selectedIds.filter(id => id !== itemId));
    } else {
      onSelectedIdsChange([...selectedIds, itemId]);
    }
  };
}

export const PageListItemRenderer = memo(function PageListItemRenderer(
  item: ListItem
) {
  const props = useAtomValue(listsPropsAtom);
  const { selectionActive } = useAtomValue(selectionStateAtom);
  const pageItemIds = useAtomValue(pageItemIdsAtom);

  const page = item as DocMeta;
  return (
    <PageListItem
      {...pageMetaToListItemProp(
        page,
        {
          ...props,
          selectable: !!selectionActive,
        },
        pageItemIds
      )}
    />
  );
});

export const CollectionListItemRenderer = memo(
  function CollectionListItemRenderer(item: ListItem) {
    const props = useAtomValue(listsPropsAtom);
    const { selectionActive } = useAtomValue(selectionStateAtom);
    const collection = item as CollectionMeta;
    return (
      <CollectionListItem
        {...collectionMetaToListItemProp(collection, {
          ...props,
          selectable: !!selectionActive,
        })}
      />
    );
  }
);

export const TagListItemRenderer = memo(function TagListItemRenderer(
  item: ListItem
) {
  const props = useAtomValue(listsPropsAtom);
  const { selectionActive } = useAtomValue(selectionStateAtom);
  const tag = item as TagMeta;
  return (
    <TagListItem
      {...tagMetaToListItemProp(tag, {
        ...props,
        selectable: !!selectionActive,
      })}
    />
  );
});

function tagIdToTagOption(
  tagId: string,
  docCollection: DocCollection
): Tag | undefined {
  return docCollection.meta.properties.tags?.options.find(
    opt => opt.id === tagId
  );
}

const PageTitle = memo(function PageTitle({ id }: { id: string }) {
  const doc = useLiveData(useService(DocsService).list.doc$(id));
  const title = useLiveData(doc?.title$);
  const t = useI18n();
  return title || t['Untitled']();
});

const UnifiedPageIcon = memo(function UnifiedPageIcon({
  id,
}: {
  id: string;
  docCollection: DocCollection;
}) {
  const list = useService(DocsService).list;
  const isEdgeless = useLiveData(list.primaryMode$(id)) === 'edgeless';
  return isEdgeless ? (
    <EdgelessIcon />
  ) : (
    <HugeiconsIcon
      icon={Note04Icon}
      size={24}
      strokeWidth={1.5}
      style={{ color: 'currentColor' }}
    />
  );
});

function pageMetaToListItemProp(
  item: DocMeta,
  props: RequiredProps<DocMeta>,
  pageIds?: string[]
): PageListItemProps {
  const toggleSelection = makeToggleSelection(
    item.id,
    props.selectedIds,
    props.onSelectedIdsChange
  );
  return {
    pageId: item.id,
    pageIds,
    title: <PageTitle id={item.id} />,
    preview: (
      <PagePreview docCollection={props.docCollection} pageId={item.id} />
    ),
    createDate: new Date(item.createDate),
    updatedDate: item.updatedDate ? new Date(item.updatedDate) : undefined,
    to: props.rowAsLink && !props.selectable ? `/${item.id}` : undefined,
    onClick: toggleSelection,
    icon: <UnifiedPageIcon id={item.id} docCollection={props.docCollection} />,
    tags:
      item.tags
        ?.map(id => tagIdToTagOption(id, props.docCollection))
        .filter((v): v is Tag => v != null) ?? [],
    operations: props.operationsRenderer?.(item),
    selectable: props.selectable,
    selected: props.selectedIds?.includes(item.id),
    onSelectedChange: toggleSelection,
    draggable: props.draggable,
    isPublicPage: !!item.isPublic,
  };
}

function collectionMetaToListItemProp(
  item: CollectionMeta,
  props: RequiredProps<CollectionMeta>
): CollectionListItemProps {
  const toggleSelection = makeToggleSelection(
    item.id,
    props.selectedIds,
    props.onSelectedIdsChange
  );
  return {
    collectionId: item.id,
    title: item.title,
    to:
      props.rowAsLink && !props.selectable
        ? `/collection/${item.id}`
        : undefined,
    onClick: toggleSelection,
    icon: <ViewLayersIcon />,
    operations: props.operationsRenderer?.(item),
    selectable: props.selectable,
    selected: props.selectedIds?.includes(item.id),
    onSelectedChange: toggleSelection,
    draggable: props.draggable,
  };
}

function tagMetaToListItemProp(
  item: TagMeta,
  props: RequiredProps<TagMeta>
): TagListItemProps {
  const toggleSelection = makeToggleSelection(
    item.id,
    props.selectedIds,
    props.onSelectedIdsChange
  );
  return {
    tagId: item.id,
    title: item.title,
    to: props.rowAsLink && !props.selectable ? `/tag/${item.id}` : undefined,
    onClick: toggleSelection,
    color: item.color,
    pageCount: item.pageCount,
    operations: props.operationsRenderer?.(item),
    selectable: props.selectable,
    selected: props.selectedIds?.includes(item.id),
    onSelectedChange: toggleSelection,
    draggable: props.draggable,
  };
}
